/**
 * Inspection Template Types
 *
 * Templates define the structure of an inspection with sections and items.
 * This schema represents the RESOLVED template - the materialized view
 * that clients use for rendering and execution.
 *
 * PERSISTENCE: Templates are stored as event streams (additions, modifications).
 * PROJECTION: This schema is the resolved output - full sections ready to render.
 *
 * For child templates, the projection is computed at read time:
 *   parent@version + child events = resolved InspectionTemplate
 */

import type {
  SchemaVersion,
  AssetType,
  SourceEntityIdentifier,
} from './common';
import type { QuestionType, QuestionConfig } from './questionTypes';

/**
 * Template type identifiers for catalog/filtering
 */
export enum TemplateType {
  ABBREVIATED_INSPECTION = 'abbreviated_inspection',
  FULL_INSPECTION = 'full_inspection',
}

/**
 * Domain question types mapped from gRPC QuestionType enum
 */
export enum DomainQuestionType {
  RATING_5_POINT = 'rating_5_point',
  RATING_3_POINT = 'rating_3_point',
  PASS_FAIL = 'pass_fail',
  OK_DEFICIENT = 'ok_deficient',
  CONDITION_ACTION = 'condition_action',
  YES_NO = 'yes_no',
  CHECKLIST = 'checklist',
  TEXT_ONLY = 'text_only',
  NUMERIC = 'numeric',
  DATE = 'date',
  SELECT = 'select',
  MULTI_SELECT = 'multi_select',
  OBSERVATION = 'observation',
}

/**
 * Domain asset types mapped from gRPC AssetType enum
 */
export enum DomainAssetType {
  INDUSTRIAL = 'industrial',
  RETAIL = 'retail',
  OFFICE = 'office',
  RESIDENTIAL = 'residential',
  MIXED_USE = 'mixed_use',
}

/**
 * Origin enum: distinguishes inherited vs locally-defined sections/items
 * Server-computed; never client-supplied.
 */
export enum Origin {
  UNSPECIFIED = 'UNSPECIFIED',
  INHERITED = 'INHERITED',
  LOCAL = 'LOCAL',
}

/**
 * Inspection Template (Projection)
 */
export interface InspectionTemplate {
  /** Unique identifier for this template */
  id: string; // UUID v4

  /** Schema version for client compatibility */
  schemaVersion: SchemaVersion;

  /** This template's version (increments on each publish) */
  version: number; // Unsigned integer, increments on each publish

  // ─── Inheritance ────────────────────────────────────────────────────

  /** Parent template ID (if this template inherits from another) */
  parentTemplateId?: string;

  /**
   * Whether this template is locked from parent updates.
   * - false (default): Inherits changes from parent template automatically
   * - true: Frozen at parentTemplateLockedVersion, will not receive parent updates
   */
  locked: boolean;

  /**
   * Parent template version this template is locked to.
   * Only present when locked: true.
   */
  parentTemplateLockedVersion?: number;

  /**
   * Whether this template is hidden from the building's inspectors.
   * Hidden templates remain visible and manageable in the admin experience;
   * they are excluded from inspector-facing listings.
   */
  hidden: boolean;

  // ─── Structure (resolved) ───────────────────────────────────────────

  /** Resolved sections (inherited + local, with modifications applied) */
  sections: TemplateSection[];

  /** Template-level configuration (with overrides applied) */
  config: TemplateConfig;

  // ─── Non-critical Metadata ──────────────────────────────────────────

  /** Display and categorization metadata */
  metadata: TemplateMetadata;
}

/**
 * Template Metadata (Non-Critical)
 *
 * Display, categorization, and attribution information.
 * Not required for rendering or execution.
 */
export interface TemplateMetadata {
  /** Human-readable template name */
  name: string;

  /** Optional description */
  description?: string;

  /** Template type identifier (for catalog/filtering) */
  templateType?: string;

  /**
   * Asset types this template applies to.
   * Used on PARENT templates for catalog filtering and assignment validation.
   * Not needed on building templates - building asset type comes from CES.
   */
  assetTypes?: AssetType[];

  /** Tags for categorization and search */
  tags?: string[];

  /**
   * Building ID if this is a building-level template.
   * Links template to CES building record.
   */
  buildingId?: SourceEntityIdentifier;

  /** Initial creation timestamp */
  createdAt?: string; // ISO 8601

  /** User who created the template */
  createdBy?: string; // User ID

  /** Last update timestamp */
  updatedAt?: string; // ISO 8601

  /** User who last updated the template */
  updatedBy?: string; // User ID
}

export interface TemplateConfig {
  /** Whether photos are required for deficient items */
  requirePhotoOnDeficiency: boolean;

  /** Maximum photos per item */
  maxPhotosPerItem: number;

  /** Whether notes are required for deficient items */
  requireNoteOnDeficiency: boolean;

  /** Default question type for new items */
  defaultQuestionType: QuestionType;

  /** Allow observers to add ad-hoc items during execution */
  allowObservationItems: boolean;

  /** Completion rules */
  completionRules: CompletionRules;
}

export interface CompletionRules {
  /**
   * If true, all items must have a response (including N/A) to complete.
   * If false, only items marked as `required: true` need responses.
   */
  allItemsRequired: boolean;

  /**
   * If true, inspector can submit with some items unanswered.
   * Useful for templates where some sections may not apply.
   */
  allowPartialSubmission: boolean;
}

/**
 * Template Section
 */
export interface TemplateSection {
  /** Stable UUID for this section */
  id: string; // UUID v4

  /** Section display name */
  name: string;

  /** Optional section description/instructions */
  description?: string;

  /** Display order (0-indexed) */
  order: number;

  /** Ordered list of items in this section */
  items: TemplateItem[];

  /** Section-level configuration overrides */
  config?: SectionConfig;

  /**
   * Tombstone row from event aggregation when hidden entities are included in the response.
   * Omitted or false means live.
   */
  removed?: boolean;

  /** Server-computed origin: distinguishes inherited vs locally-defined sections */
  origin?: Origin;
}

export interface SectionConfig {
  /** Override template-level photo requirements */
  requirePhotoOnDeficiency?: boolean;

  /** Whether this section can be skipped entirely */
  skippable: boolean;

  /** Collapse section by default in UI */
  collapsedByDefault: boolean;
}

/**
 * Template Item (Inspection Question/Checkpoint)
 */
export interface TemplateItem {
  /** Stable UUID for this item */
  id: string; // UUID v4

  /** Item display text (the question/checkpoint) */
  label: string;

  /** Optional help text or instructions */
  helpText?: string;

  /** Display order within section (0-indexed) */
  order: number;

  /** Question type from the catalog */
  questionType: QuestionType;

  /**
   * Question type configuration.
   * Required for: numeric, select, multi_select, date
   * Not needed for: rating_*, pass_fail, ok_deficient, yes_no, checklist,
   *                 condition_action, text_only, observation
   */
  questionConfig?: QuestionConfig;

  /** Whether this item requires a response */
  required: boolean;

  /** Whether photos can be attached */
  photosEnabled: boolean;

  /** Whether notes/comments are enabled */
  notesEnabled: boolean;

  /** Item-level configuration */
  config?: ItemConfig;

  /** Tombstone item; same semantics as {@link TemplateSection.removed}. */
  removed?: boolean;

  /** Server-computed origin: distinguishes inherited vs locally-defined items */
  origin?: Origin;
}

export interface ItemConfig {
  /** Override: require photo when deficient */
  requirePhotoOnDeficiency?: boolean;

  /** Override: require note when deficient */
  requireNoteOnDeficiency?: boolean;

  /** Minimum photos required */
  minPhotos?: number;

  /** Regulatory/compliance flag */
  isComplianceItem: boolean;

  /** Tags for categorization and reporting */
  tags?: string[];

  /** Category/subcategory for grouping in reports */
  category?: string;
}

/**
 * Template Reference (used in instances)
 */
export interface TemplateReference {
  /** Template ID */
  templateId: string;

  /** Exact version used for this instance (immutable after creation) */
  version: number;

  /** Denormalized template name for display */
  templateName?: string;
}

/**
 * Events that mutate template state (for event sourcing)
 *
 * Design principles:
 * - Atomic: Each event represents one logical change
 * - Minimal: No computed/derived events (version is event count)
 * - Separated: Metadata updates are distinct (important for inheritance)
 */
export type TemplateEvent =
  // Template lifecycle
  | {
      type: 'TEMPLATE_CREATED';
      payload: CreateTemplatePayload;
      timestamp: string;
      userId: string;
    }

  // Section mutations
  | {
      type: 'SECTION_ADDED';
      payload: TemplateSection;
      timestamp: string;
      userId: string;
    }
  | {
      type: 'SECTION_UPDATED';
      payload: { sectionId: string; changes: Partial<TemplateSection> };
      timestamp: string;
      userId: string;
    }
  | {
      type: 'SECTION_REMOVED';
      payload: { sectionId: string };
      timestamp: string;
      userId: string;
    }

  // Item mutations
  | {
      type: 'ITEM_ADDED';
      payload: { sectionId: string; item: TemplateItem };
      timestamp: string;
      userId: string;
    }
  | {
      type: 'ITEM_UPDATED';
      payload: {
        sectionId: string;
        itemId: string;
        changes: Partial<TemplateItem>;
      };
      timestamp: string;
      userId: string;
    }
  | {
      type: 'ITEM_REMOVED';
      payload: { sectionId: string; itemId: string };
      timestamp: string;
      userId: string;
    }

  // Config mutations
  | {
      type: 'CONFIG_UPDATED';
      payload: Partial<TemplateConfig>;
      timestamp: string;
      userId: string;
    }

  // Metadata mutations (separate for child template inheritance)
  | {
      type: 'METADATA_UPDATED';
      payload: Partial<TemplateMetadata>;
      timestamp: string;
      userId: string;
    }

  // Inheritance mutations
  | {
      type: 'TEMPLATE_LOCKED';
      payload: { parentVersion: number };
      timestamp: string;
      userId: string;
    }
  | {
      type: 'TEMPLATE_UNLOCKED';
      payload: Record<string, never>;
      timestamp: string;
      userId: string;
    };

export interface CreateTemplatePayload {
  id: string;
  parentTemplateId?: string;
  locked: boolean;
  config: TemplateConfig;
  metadata: TemplateMetadata;
}
